import React, { useState, useEffect, useCallback } from 'react';
import { DFPAPIEndpointGalley } from '../../../../utils/axios';
import { parseJwtToken, calculateTextWidth, storageUtil } from '../../../../utils/commonFunctions';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import CustomSelect from '../../../../common/CustomSelect';
import LoadingGIF from '../../../../assets/loadingIn.gif';
import useToastAssist from '../../../../utils/useToastAssist';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { StandardGalleyLoading } from '../standardGalleyLoading';
import { TrolleyLoading } from '../trolleyLoading';


const getHeaders = async () => {
  try {
    const storedCreds = localStorage.getItem('login_credentials');
    if (!storedCreds) return null;
    const parsedCreds = JSON.parse(storedCreds);
    const { data } = parsedCreds || {};
    if (!data) return null;
    const tokenResp = await parseJwtToken(data);
    if (!tokenResp?.userId) return null;
    return {
      user_id: tokenResp.userId,
      DashboardAccess: tokenResp.userType
    };
  } catch (err) {
    console.error('getHeaders error:', err);
    return null;
  }
};

function FormGroup({ label, required, hint, children }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}{required && <span className="required">*</span>}
      </label>
      {children}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}


const CLASS_CODES = [
  { value: "ecy", label: "Economy" },
  { value: "bus", label: "Business Class" },
  { value: "fst", label: "First Class" },
  { value: "pem", label: "Premium Economy" },
];
const getCabinClassLabel = (classCode) => {
  const cabinClass = CLASS_CODES.find(c => c.value === classCode);
  return cabinClass ? cabinClass.label : 'Unknown';
};

  const getCustomAircraftStyles = (minWidth = "40rem") => {
  return {
    control: (defaultStyles, state) => ({
      ...defaultStyles,
      width: minWidth,
      minWidth: minWidth,
      backgroundColor: '#0a1423',
      borderColor: 'rgba(76,195,230,0.3)',
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    singleValue: (defaultStyles) => ({
      ...defaultStyles,
      color: '#d6eaf8',
      textAlign: 'left',
    }),
    placeholder: (defaultStyles) => ({
      ...defaultStyles,
      color: '#8aaacf',
      textAlign: 'left',
    }),
  };
};

const CateringListing = () => {
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [galleys, setGalleys] = useState([]);
  const [trolleyUnits, setTrolleyUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [aircraftWidth, setAircraftWidth] = useState("20rem");
  const [viewTab, setViewTab] = useState('galley');
  const [editingGalley, setEditingGalley] = useState(null);
  const [galleyModal, setGalleyModal] = useState(false);
  const [savingGalley, setSavingGalley] = useState(false);
    const {toastError,toastSuccess} = useToastAssist();
  const [saveConfirm, setSaveConfirm] = useState({
  show: false,
  galleyName: '',
  isLoading: false
});

  const handleTrolleyView = useCallback(async (trolley, galley) => {
    if (!trolley || !galley || !selectedAircraft) return;

    let aircraftData = selectedAircraft;

    // Always fetch fresh aircraft data so that previously saved trolley
    // slot items are reflected immediately on re-open.
    try {
      const aircraftTypeId = selectedAircraft?.data?.id || selectedAircraft?.value;
      const response = await DFPAPIEndpointGalley.get(
        `/galley/aircraftTypes/${aircraftTypeId}`
      );
      if (response?.data?.galleys) {
        aircraftData = {
          ...selectedAircraft,
          data: {
            ...selectedAircraft.data,
            galleys: response.data.galleys,
          },
        };
      }
    } catch (error) {
      // Non-fatal: fall back to cached aircraft data
    }
    
    setSelectedTrolleyData({
      aircraftId: selectedAircraft?.data?.id || selectedAircraft?.value,
      trolleyId: trolley.allotId,  
      trolleyType: trolley.type,  
      trolleyName: trolley.id,
      galleyId: galley._id || galley.galleyId,
      galleyName: galley.name,
      trolleyData: trolley,
      selectedAircraft: aircraftData  
    });
    setShowTrolleyLoadingModal(true);
  }, [selectedAircraft, toastError]);

  const [galleyForm, setGalleyForm] = useState({
    name: '',
    position: 'fwd',
    classCodes: ['ecy'],
    fullCarts: 0,
    halfCarts: 0,
    smu: 0,
    stowage: 0,
    imageFile: null,
    imageName: ''
  });
  const [deleteModal, setDeleteModal] = useState({
  show: false,
  galleyId: null,
  galleyName: '',
  isLoading: false
});
  const [showGalleyLoadingModal, setShowGalleyLoadingModal] = useState(false);
  const [showTrolleyLoadingModal, setShowTrolleyLoadingModal] = useState(false);
  const [selectedGalleyData, setSelectedGalleyData] = useState(null);
  const [selectedTrolleyData, setSelectedTrolleyData] = useState(null);

useEffect(() => {
  if (aircraftTypes.length === 0) {
    setAircraftWidth("30rem");
    return;
  }

  let longestLabel = "";

  if (selectedAircraft) {
    longestLabel = selectedAircraft.label; 
  } else {
    longestLabel = aircraftTypes.reduce((max, current) => 
      current.label.length > max.length ? current.label : max, 
      ""
    );
  }
  const calculatedWidth = calculateTextWidth(longestLabel);
  setAircraftWidth(`${calculatedWidth + 100}px`);
}, [aircraftTypes, selectedAircraft]);

  const fetchAircraftTypes = useCallback(async () => {
    try {
      setLoading(true);
      const hdrs = await getHeaders();
      if (!hdrs) {
        return;
      }

      const filterData = {
        action: 4,
        collection: '',
        filterOperation: 'or',
        filters: [],
        limit: 50,
        page: 1,
        searchParam: ''
      };

      const probe = await DFPAPIEndpointGalley.post('galley/aircraftTypes/process', { ...filterData, limit: 10 }, hdrs);
      const totalCount = probe?.count || probe?.items?.length || 10;

      const full = await DFPAPIEndpointGalley.post(
        'galley/aircraftTypes/process',
        { ...filterData, limit: totalCount },
        hdrs
      );

      if (full?.items?.length) {
        const mapped = full.items.map(item => ({
          value: item.id,
          label: `${item.key || item.model}`,
          data: {
            id: item.id,
            code: item.key || item.model,
            variant: item.variant,
            body: item.bodyType,
            seats: item.capacity,
            galleys: item.galleys || []
          }
        }));

        setAircraftTypes(mapped);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);
   
  const handleAircraftChange = useCallback((selected) => {
    if (!selected) {
      setSelectedAircraft(null);
      setGalleys([]);
      setTrolleyUnits([]);
      setSearchTerm('');
      setStatusFilter('all');
      setViewTab('galley');
      return;
    }

    setSelectedAircraft(selected);
    setSearchTerm('');
    setStatusFilter('all');
    setViewTab('galley');

    const galleys = selected.data?.galleys || [];
    setGalleys(galleys);

    const trolleys = [];
    galleys.forEach((galley, galleyIdx) => {
    
      (galley.bayAreas || []).forEach((bayArea) => {
        const trolleysInBay = bayArea.trolleys || [];
        trolleysInBay.forEach((trolley) => {
          const cartAllots = trolley.cartQuantityAllots || [];
          cartAllots.forEach((allot) => {
            trolleys.push({
              id: allot.code || `trolley-${galleyIdx}-${trolley.id}`,
              type: trolley.name || trolley.id,
              category: 'Standard',
              qty: allot.typesOfItems?.length || 0,
              galley: galley.name || `Galley-${galleyIdx}`,
              galleyId: galley._id,
              cartTypeId: trolley.id,
              allotId: allot._id,
              weight: allot.weight || 0,
              volume: allot.volume || 0,
              bayAreaName: bayArea.name,
            });
          });
        });
      });
    });


    setTrolleyUnits(trolleys);
  },[]);
 
useEffect(() => {
  const selectedAircraftReturn = storageUtil.get('selectedAircraftReturn','session');
  
  if (selectedAircraftReturn && aircraftTypes.length > 0) {
    try {
      const { id, view } = selectedAircraftReturn;
      const matchedAircraft = aircraftTypes.find(ac => ac.value === id);
      
      if (matchedAircraft) {

        handleAircraftChange(matchedAircraft);

        if (view === 'trolley') {
          setViewTab('trolley');
        } else if (view === 'galley') {
          setViewTab('galley');
        }

        storageUtil.clear('selectedAircraftReturn','session');
        
        console.log('Aircraft restored:', matchedAircraft.label, 'View:', view);
      }
    } catch (error) {
      console.error(' Error parsing selectedAircraftReturn:', error);
    }
  }
}, [aircraftTypes, handleAircraftChange])


async function refetchAircraftData(aircraftTypeId) {
  try {
    const hdrs = await getHeaders();
    if (!hdrs) return;
 
    const firstResponse = await DFPAPIEndpointGalley.post(
      'galley/aircraftTypes/process',
      {
        action: 4,
        collection: '',
        filterOperation: 'or',
        filters: [{ key: 'id', value: aircraftTypeId }],
        limit: 10,  // Small limit first
        page: 1,
        searchParam: ''
      },
      hdrs
    );

    if (!firstResponse?.items?.length) {
      return;
    }
 
    const totalCount = firstResponse.count || firstResponse.items.length;
 
    let allItems = [...firstResponse.items];
 
    const pageSize = 10;
    const totalPages = Math.ceil(totalCount / pageSize);
 
    for (let page = 2; page <= totalPages; page++) {
      const response = await DFPAPIEndpointGalley.post(
        'galley/aircraftTypes/process',
        {
          action: 4,
          collection: '',
          filterOperation: 'or',
          filters: [{ key: 'id', value: aircraftTypeId }],
          limit: pageSize,
          page: page,
          searchParam: ''
        },
        hdrs
      );

      if (response?.items?.length) {
        allItems = [...allItems, ...response.items];
      
      }
    }
 

    if (allItems.length > 0) {
      const updatedAircraft = allItems.find(ac => ac.id === aircraftTypeId);
      
      if (updatedAircraft) {
     
        const updatedSelected = {
          ...selectedAircraft,
          data: updatedAircraft
        };
        
        setSelectedAircraft(updatedSelected);
        setGalleys(updatedAircraft.galleys || []);
      
    const trolleys = [];
(updatedAircraft.galleys || []).forEach((galley) => {
 
  (galley.bayAreas || []).forEach((bayArea) => {
 
    (bayArea.trolleys || []).forEach((trolley) => {
      const cartAllots = trolley.cartQuantityAllots || [];
      cartAllots.forEach((allot) => {
        trolleys.push({
          id: allot.code,
          type: trolley.name,
          category: 'Standard',
          qty: allot.typesOfItems?.length || 0,
          galley: galley.name,
          galleyId: galley._id,
          cartTypeId: trolley.id,
          allotId: allot._id,
          weight: allot.weight || 0,
          volume: allot.volume || 0,
          bayAreaName: bayArea.name, 
        });
      });
    });
  });
});
        
        setTrolleyUnits(trolleys);

      }
    }
  } catch (err) {

  }
}

async function handleAddGalley() {
  if (!galleyForm.name.trim()) return;

  if (editingGalley) {
    setSaveConfirm({
      show: true,
      galleyName: editingGalley.name,
      isLoading: false
    });
    return;  
  }
 
  await saveGalley();
}

async function saveGalley() {
  if (!galleyForm.name.trim()) return;
  
  const aircraftTypeId = selectedAircraft?.data?.id || selectedAircraft?.value;
  
  if (!aircraftTypeId) {
    toastError('Please select an aircraft first');
    return;
  }
  
  setSavingGalley(true);
  try {
    const hdrs = await getHeaders();
    if (!hdrs) return;

    const createCartItem = (id, name, quantity) => ({
      id, name, weight: 0, volume: 0,
      dimensions: { length: 0, breadth: 0, height: 0 },
      quantity: parseInt(quantity) || 0,
    });

    const carts = [
      createCartItem("full",    "Full Cart",     galleyForm.fullCarts),
      createCartItem("half",    "Half Cart",     galleyForm.halfCarts),
      createCartItem("smu",     "SMU",           galleyForm.smu),
      createCartItem("stowage", "Misc. Stowage", galleyForm.stowage),
    ];

    const classCode = galleyForm.classCodes?.[0] || "ecy";

    let res;
    if (editingGalley) {
      res = await DFPAPIEndpointGalley.post(
        `galley/aircraftTypes/${aircraftTypeId}/galleys`, 
        {
          name:            galleyForm.name.trim(),
          classCode,
          carts,
          galleyCartConfig: editingGalley.galleyCartConfig || { userDefined: [], configured: [] },
          audit:           editingGalley.audit || undefined,
          trolleyUnits:    editingGalley.trolleyUnits || [],
          galleyId:        editingGalley._id,
        }, 
        hdrs
      );
    } else {
      res = await DFPAPIEndpointGalley.post(
        `galley/aircraftTypes/${aircraftTypeId}/galleys`, 
        {
          name:      galleyForm.name.trim(),
          classCode,
          carts,
        }, 
        hdrs
      );
    }

    if (res?.success) {
      toastSuccess(res.message || (editingGalley ? 'Galley updated successfully.' : 'Galley added successfully.'));
      setGalleyModal(false);
      setEditingGalley(null);
      setGalleyForm({ 
        name: "", 
        position: "fwd", 
        classCodes: ["ecy"], 
        fullCarts: 0, 
        halfCarts: 0, 
        smu: 0, 
        stowage: 0, 
        imageFile: null, 
        imageName: "" 
      });
      
 
      await refetchAircraftData(aircraftTypeId);
    }
  } catch (err) {
    
    const e = JSON.parse(err?.message);
    toastError(e?.data?.message || 'Failed to save galley.');
  } finally {
    setSavingGalley(false);
  }
}

 
async function confirmSaveGalley() {
  setSaveConfirm({ show: false, galleyName: '', isLoading: false });
  
  try {
    await saveGalley();
  } catch (err) {
    console.error('Error saving:', err);
  }
}

function cancelSaveGalley() {
  setSaveConfirm({ show: false, galleyName: '', isLoading: false });
}

  const openEditGalley = (galley,mode='view') => {

if(selectedAircraft && galley && mode ==='view'){
  let aircraftData = selectedAircraft;

  if (!selectedAircraft?.data?.galleys || selectedAircraft.data.galleys.length === 0) {
  
    aircraftData = {
      ...selectedAircraft,
      data: {
        ...selectedAircraft.data,
        galleys: galleys 
      }
    };
  }
  
  setSelectedGalleyData({
    aircraftId: selectedAircraft?.data?.id || selectedAircraft?.value,
    galleyId: galley._id,
    galleyName: galley.name,
    galleyData: galley,
    selectedAircraft: aircraftData 
  });
  setShowGalleyLoadingModal(true);
}else{
      if (!galley || !selectedAircraft) return;
 
    const getTrolleyCount = (typeId) => {
      const allTrolleys = [];
      (galley.bayAreas || []).forEach(bay => {
        allTrolleys.push(...(bay.trolleys || []));
      });
      return allTrolleys
        .filter(t => t.id === typeId)
        .reduce((sum, t) => sum + (t.quantity || 0), 0);
    };

    setGalleyForm({
      name: galley.name || '',
      position: galley.position?.code?.toLowerCase() || 'fwd',
      classCodes: galley.classCodes || ['ecy'],
      fullCarts: getTrolleyCount('full'),
      halfCarts: getTrolleyCount('half'),
      smu: getTrolleyCount('smu'),
      stowage: getTrolleyCount('stowage'),
      imageFile: null,
      imageName: galley.imageUrl ? galley.imageUrl.split('/').pop() : ''
    });

    setEditingGalley(galley);

    setGalleyModal(true);
  }
  };

  useEffect(() => {
    fetchAircraftTypes();
    const close = () => setOpenDropdown(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [fetchAircraftTypes]);


  async function handleDeleteGalley(galley) {
 
  setDeleteModal({
    show: true,
    galleyId: galley._id,
    galleyName: galley.name,
    isLoading: false
  });
}



async function confirmDeleteGalley() {
  try {
    const aircraftTypeId = selectedAircraft?.data?.id || selectedAircraft?.value;
    const galleyId = deleteModal.galleyId;

    if (!aircraftTypeId || !galleyId) {
      toastError('Missing required data');
      return;
    }

    setDeleteModal(prev => ({ ...prev, isLoading: true }));

    const hdrs = await getHeaders();
    if (!hdrs) return;

    const res = await DFPAPIEndpointGalley.delete(
      `galley/aircraftTypes/${aircraftTypeId}/galleys/${galleyId}`,
      hdrs
    );

    if (res?.success) {
      toastSuccess(res.message || 'Galley removed.');
 
      setGalleys(prev => prev.filter(g => g._id !== galleyId));
      setTrolleyUnits(prev => prev.filter(t => t.galleyId !== galleyId));
 
      setDeleteModal({ show: false, galleyId: null, galleyName: '', isLoading: false });
    }
  } catch (err) {
  
    const e = JSON.parse(err.message);
    toastError(e?.data?.message || 'Failed to remove galley.');
    setDeleteModal(prev => ({ ...prev, isLoading: false }));
  }
}

function cancelDeleteGalley() {
  setDeleteModal({ show: false, galleyId: null, galleyName: '', isLoading: false });
}

  const filteredGalleys = galleys.filter(galley => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q || 
      (galley.name?.toLowerCase().includes(q)) ||
      (galley.position?.code?.toLowerCase().includes(q)) ||
      (galley.cabinClass?.toLowerCase().includes(q));

    const matchesStatus = statusFilter === 'all' || (galley.status?.toLowerCase() === statusFilter);

    return matchesSearch && matchesStatus;
  });

const getGalleyIndex =(galName)=>{
  const match = galName.match(/\b(G\d+[A-Z]*)\b/i);
  return match ? match[1].toUpperCase() : "--";
}

  return (
    <div className="catering-shell"  style={{
  width: '100%',
  height: '100%',
  display: 'flex',
  padding:'16px',
  flexDirection: 'column'
}}>
              <ToastContainer style={{ zIndex: 999999 }} />
             <div className="catering-main" style={{
                                        width: '100%',
                                        minWidth: '100%',
                                        flex: '1',
                                        display: 'flex',
                                        flexDirection: 'column'
                                      }}>        
                   <>
 
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',   
                        marginBottom: 24, 
                        gap: 32,
                        flexWrap: 'nowrap'   
                      }}>
                 
                        <div style={{ flex: '0 0 auto' }}>
                          <label className="req mb-2 margBottom10">
                            Aircraft Variant *
                            {!aircraftTypes?.length ? (
                              <img src={LoadingGIF} style={{ width: '16px', height: '16px', marginLeft: '10px' }} alt="Loading" />
                            ) : (
                              <></>
                            )}
                          </label>  

                          <CustomSelect
                            options={aircraftTypes}
                            value={selectedAircraft}
                            onChange={handleAircraftChange}
                            controlStyle={{ border: "1px solid rgba(40,80,140,0.45)", borderRadius: 7 }}
                            placeholder="Select Aircraft Variant"
                            isDisabled={loading}
                            width={aircraftWidth} 
                            styles={getCustomAircraftStyles(aircraftWidth)}
                          />
                        </div>
 
                        <div style={{ 
                          flex: '0 0 auto',         
                          display: 'flex', 
                          gap: 12, 
                          alignItems: 'center',       
                          paddingTop: '24px'        
                        }}>
               
                          <div style={{ 
                            display: 'flex', 
                            gap: 8, 
                            border: '1px solid rgba(40,80,140,0.3)', 
                            borderRadius: 6, 
                            padding: 4, 
                            marginBottom: 0       
                          }}>
                            {['galley', 'trolley'].map(tab => (
                              <button
                                key={tab}
                                onClick={() => setViewTab(tab)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: 4,
                                  border: 'none',
                                  background: viewTab === tab ? 'rgba(76,195,230,0.2)' : 'transparent',
                                  color: viewTab === tab ? '#4cc3e6' : '#8aaacf',
                                  fontSize: 12,
                                  fontWeight: viewTab === tab ? 600 : 400,
                                  cursor: 'pointer',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* GALLEY VIEW */}
                      {viewTab === 'galley' && (
                        <>
                        <div className='listing-header'>
                          <button
                            className="btn btn-primary"
                            onClick={() => { setEditingGalley(null); setGalleyForm({ name: '', position: 'fwd', classCodes: ['ecy'], fullCarts: 0, halfCarts: 0, smu: 0, stowage: 0, imageFile: null, imageName: '' }); setGalleyModal(true); }}
                            disabled={loading || !Boolean(selectedAircraft)}
                          >
                            + Add Galley
                          </button>
                        </div>

                          <div className="listing-toolbar width100">
                            <div className="listing-search">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3d5a7a" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                              </svg>
                              <input
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search by galley name, position, or cabin…"
                              />
                            </div>

                            <button className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                              Filters
                            </button>
                          </div>

                          <div className="table-wrap" style={{ minWidth: '100%',width: '100%', overflow: 'auto'}}>
                            {loading ? (
                              <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                                Loading galleys…
                              </div>
                            ) : !selectedAircraft ? (
                                <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                                  Please select an aircraft variant
                                </div>
                              ): filteredGalleys.length === 0 ? (
                              <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                                No galleys found for {selectedAircraft?.label}.
                              </div>
                            ) : (
                              <table className="data-table catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
                                <colgroup>
                                <col style={{ width:"30%" }} />
                                <col style={{ width:"30%" }} />
                                <col style={{ width:"20%" }} />
                                <col style={{ width:"15%" }} />
                                <col style={{ width:"20%" }} />

                              </colgroup>
                                <thead>
                                  <tr>
                                    <th>Galley</th>
                                    <th>Cabin Class</th>
                                    <th>Trolleys</th>
                                    <th>SMU</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredGalleys.map((galley, idx) => {

                     
                                    const allTrolleys = [];
                                    (galley.bayAreas || []).forEach(bay => {
                                      allTrolleys.push(...(bay.trolleys || []));
                                    });

                         
                                    const getTrolleyCount = (typeId) => {
                                      return allTrolleys
                                        .filter(t => t.id === typeId)
                                        .reduce((sum, t) => sum + (t.quantity || 0), 0);
                                    };

                                    const fullCarts = getTrolleyCount('full');
                                    const halfCarts = getTrolleyCount('half');
                                    const smuCount = getTrolleyCount('smu');
                                
                                    return (
                                      <tr key={galley.id || idx}>
                                        <td><span style={{ fontWeight: 600, color: '#4cc3e6' }}>{getGalleyIndex(galley?.name ||"")}</span><div style={{ fontSize: 12, color: '#8aaacf', marginTop: 2 }}>{galley.name || 'Galley'}</div></td>
                                        <td className="td-secondary"><span className="status-badge" style={{ background: 'rgba(76,195,230,0.15)', border: '1px solid rgba(76,195,230,0.3)', color: '#4cc3e6', fontSize: 12 }}>{galley.classCode ? getCabinClassLabel(galley.classCode) : '-'}</span></td>
                                        <td className="td-secondary">{fullCarts + halfCarts} <span style={{ fontSize: 11, color: '#3d5a7a' }}>{fullCarts} full {halfCarts > 0 ? ': ' + halfCarts + ' half' : ''}</span></td>
                                        <td className="td-secondary">{smuCount}</td>
                                        <td>
                                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button
                                              className="btn-view"
                                              onClick={() => openEditGalley(galley,'view')}
                                            >
                                              View
                                            </button>
                                            <div className="dropdown-wrap" onClick={e => e.stopPropagation()}>
                                              <button
                                                className="dropdown-trigger"
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  setOpenDropdown(openDropdown === idx ? null : idx);
                                                }}
                                              >
                                                ···
                                              </button>
                                              {openDropdown === idx && (
                                                    <div className="dropdown-menu-catering">
                                                      <div 
                                                        className="dropdown-item-catering" 
                                                        onClick={() => { openEditGalley(galley,'edit'); setOpenDropdown(null); }}
                                                      >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                                                          Edit
                                                      </div>
                                                      <div 
                                                        className="dropdown-item-catering" 
                                                        style={{ color: '#e05c5c' }}
                                                        onClick={() => { handleDeleteGalley(galley); setOpenDropdown(null); }}
                                                      >
                                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                                      Delete
                                                      </div>
                                                    </div>
                                                  )}
                                                  </div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </>
                      )}

                      {/* TROLLEY VIEW */}
                      {viewTab === 'trolley' && (
                        <>
 
                          <div className="table-wrap" style={{ minWidth: '100%', width: '100%', overflow: 'auto'}}>
                              {!selectedAircraft ? (
                              <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                                Please select an aircraft variant
                              </div>
                              ):trolleyUnits.length === 0 ? (
                              <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                                No trolley units configured yet.
                              </div>
                            ) :  (
                              <table className="data-table catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
                                <colgroup>
                                <col style={{ width:"30%" }} />
                                <col style={{ width:"20%" }} />
                                <col style={{ width:"20%" }} />
                                <col style={{ width:"20%" }} />
                                <col style={{ width:"30%" }} />
                                <col style={{ width:"20%" }} />

                              </colgroup>
                                <thead>
                                  <tr>
                                    <th>Trolley Name</th>
                                    <th>Trolley Type</th>
                                    <th>Category</th>
                                    <th>Total Item Qty</th>
                                    <th>Galley</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {trolleyUnits.map((trolley, idx) => (
                                    <tr key={trolley.allotId || idx}>
                                      <td><span style={{ fontWeight: 600, color: '#4cc3e6' }}>{trolley.bayAreaName && trolley.id ? `${trolley.bayAreaName}-${trolley.id}` : (trolley.id || trolley.bayAreaName)}</span></td>
                                      <td>{trolley.type}</td>
                                      <td><span className="status-badge" style={{ background: 'rgba(76,195,230,0.15)', border: '1px solid rgba(76,195,230,0.3)', color: '#4cc3e6', fontSize: 12 }}>{trolley.category}</span></td>
                                      <td className="td-secondary">{trolley.qty}</td>
                                      <td className="td-secondary">{trolley.galley}</td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button
                                              className="btn-view"
                                              title='View Trolley'
                                              onClick={() => {
 
                                                const galley = galleys.find(g => g._id === trolley.galleyId);
                                                if (!galley) {
                                                  
                                                  return;
                                                }
                                      
                                                handleTrolleyView(trolley, galley);
                                              }}>
                                              View
                                            </button>
                                          <div className="dropdown-wrap" onClick={e => e.stopPropagation()}>
                                            <button
                                              className="dropdown-trigger"
                                              onClick={e => {
                                                e.stopPropagation();
                                                setOpenDropdown(openDropdown === `trolley-${idx}` ? null : `trolley-${idx}`);
                                              }}
                                            >
                                              ···
                                            </button>
                                            {openDropdown === `trolley-${idx}` && (
                                              <div className="dropdown-menu-catering">
                                                <div className="dropdown-item-catering">
                                                  Edit
                                                </div>
                                                <div className="dropdown-item-catering" style={{ color: '#e05c5c' }}>
                                                  Delete
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>

                          {/* <div style={{ marginTop: 16 }}>
                            <button className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }}>
                              + Add Trolley
                            </button>
                          </div> */}
                        </>
                      )}

                    
                      {(viewTab === 'smu' || viewTab === 'oven' || viewTab === 'chiller') && (
                        <div style={{ padding: 40, textAlign: "center", color: "#3d5a7a", fontSize: 13 }}>
                          {viewTab.charAt(0).toUpperCase() + viewTab.slice(1)} 
                        </div>
                      )}

                    </>
                  

                  {deleteModal.show && (
                    <ConfirmationModal
                      title={`Delete - ${deleteModal.galleyName}`}
                      message="Are you sure you want to delete this galley? This action cannot be undone."
                      galleyName={deleteModal.galleyName}
                      isLoading={deleteModal.isLoading}
                      onClose={cancelDeleteGalley}
                      onConfirm={confirmDeleteGalley}
                    />
                  )}

                    {saveConfirm.show && (
                      <ConfirmationModal
                        title={`Edit - ${saveConfirm.galleyName}`}
                        message="Editing this galley configuration will reset all associated trolley assignments and configurations for this galley. Do you want to proceed?"
                        galleyName={saveConfirm.galleyName}
                        isLoading={saveConfirm.isLoading}
                        onClose={cancelSaveGalley}
                        onConfirm={confirmSaveGalley}
                      />
                    )}


                    {showGalleyLoadingModal && selectedGalleyData && (
                      <div 
                        className="modal-overlay" 
                        onClick={() => setShowGalleyLoadingModal(false)}
                        style={{ 
                          animation: "none", 
                          transition: "none", 
                          zIndex: 999,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)'
                        }}
                      >
                        <div 
                          className="modal-box" 
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            width: '96%', 
                            maxWidth: '1800px',
                            maxHeight: "95vh", 
                            overflowY: "auto", 
                            animation: "none", 
                            transition: "none",
                            position: 'relative'
                          }}
                        >
                
                          

                          {/* Component */}
                          <StandardGalleyLoading
                            initialAircraftId={selectedGalleyData?.aircraftId}
                            galleyId={selectedGalleyData?.galleyId}
                            galleyName={selectedGalleyData?.galleyName}
                            galleyData={selectedGalleyData?.galleyData}
                            selectedAircraft={selectedGalleyData?.selectedAircraft}
                            isOpen={showGalleyLoadingModal}
                            onClose={() => {
                              setShowGalleyLoadingModal(false);
                              setSelectedGalleyData(null);
                            }}
                            onSaveSuccess={async () => {
                              if (selectedGalleyData?.aircraftId) {
                                await refetchAircraftData(selectedGalleyData.aircraftId);
                                setTimeout(() => {
                                  if (selectedAircraft?.data) {
                                    setSelectedGalleyData(prev => ({
                                      ...prev,
                                      selectedAircraft: selectedAircraft 
                                    }));
                                  }
                                }, 500)
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}


                    {showTrolleyLoadingModal && selectedTrolleyData && (
                      <div 
                        className="modal-overlay" 
                        onClick={() => setShowTrolleyLoadingModal(false)}
                        style={{ 
                          animation: "none", 
                          transition: "none", 
                          zIndex: 999,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)'
                        }}
                      >
                        <div 
                          className="modal-box" 
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            width: '95%', 
                            maxWidth: '1400px',
                            maxHeight: "95vh", 
                            overflowY: "auto", 
                            animation: "none", 
                            transition: "none",
                            position: 'relative'
                          }}
                        >
            
                          <button 
                            className="modal-close"
                            onClick={() => {
                              setShowTrolleyLoadingModal(false);
                              setSelectedTrolleyData(null);
                            }}
                            style={{ 
                              position: 'fixed',
                              top: '20px',
                              right: '30px',
                              fontSize: '28px',
                              cursor: 'pointer',
                              background: 'none',
                              border: 'none',
                              color: '#d6eaf8',
                              zIndex: 1001
                            }}
                          >
                            ×
                          </button>

            
                          <TrolleyLoading
                            aircraftId={selectedTrolleyData?.aircraftId}
                            trolleyId={selectedTrolleyData?.trolleyId}
                            trolleyType={selectedTrolleyData?.trolleyType}
                            galleyId={selectedTrolleyData?.galleyId}
                            galleyName={selectedTrolleyData?.galleyName}
                            trolleyData={selectedTrolleyData?.trolleyData}
                            selectedAircraft={selectedTrolleyData?.selectedAircraft}
                            hideGalleyNav
                            isOpen={showTrolleyLoadingModal}
                            onClose={() => {
                              setShowTrolleyLoadingModal(false);
                              setSelectedTrolleyData(null);
                            
                              if (selectedAircraft?.data?.id) {
                                refetchAircraftData(selectedAircraft.data.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  {galleyModal && (
                    <div className="modal-overlay" style={{ animation: "none", transition: "none" }} onClick={e => e.target === e.currentTarget && setGalleyModal(false)}>
                      <div className="modal-box" style={{ width: 560, maxHeight: "85vh", overflowY: "auto", animation: "none", transition: "none" }}>
                        <div className="modal-header">
                          <div>
                            <div className="modal-title">{editingGalley ? "Edit Galley" : "Add Galley"}</div>
                            <div className="modal-subtitle">{editingGalley ? `Editing ${editingGalley.name}` : "Define a new galley position for this aircraft type"}</div>
                          </div>
                          <button className="modal-close" onClick={() => { setGalleyModal(false); setEditingGalley(null); }}>×</button>
                        </div>
                        <div className="modal-body">
                          <div className="form-grid-2" style={{ marginBottom: 18 }}>
                            <FormGroup label="Galley Name" required>
                              <input type="text" placeholder="e.g. FWD Galley" value={galleyForm.name}
                                onChange={e => setGalleyForm(f => ({ ...f, name: e.target.value }))} />
                            </FormGroup>
                            <FormGroup label="Position" required>
                              <select value={galleyForm.position} onChange={e => setGalleyForm(f => ({ ...f, position: e.target.value }))}>
                                <option value="fwd">FWD — Forward</option>
                                <option value="mid">MID — Middle</option>
                                <option value="aft">AFT — Aft</option>
                              </select>
                            </FormGroup>
                          </div>

                          <FormGroup label="Cabin Class (Multi-Select)" required>
                            <div className="chip-row mt-8">
                              {CLASS_CODES.map(c => (
                                <div
                                  key={c.value}
                                  className={`chip ${(galleyForm.classCodes || []).includes(c.value) ? "chip--selected" : ""}`}
                                  onClick={() => setGalleyForm(f => {
                                    const cur = f.classCodes || [];
                                    return { ...f, classCodes: cur.includes(c.value) ? cur.filter(x => x !== c.value) : [...cur, c.value] };
                                  })}
                                >{c.label}</div>
                              ))}
                            </div>
                          </FormGroup>

                          <div className="form-grid-2" style={{ marginTop: 18 }}>
                            <FormGroup label="No. of Full Cart Trolleys">
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.fullCarts ?? ""}
                                onChange={e => setGalleyForm(f => ({ ...f, fullCarts: e.target.value.replace(/[^0-9]/g, "") }))} />
                            </FormGroup>
                            <FormGroup label="No. of Half Cart Trolleys">
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.halfCarts ?? ""}
                                onChange={e => setGalleyForm(f => ({ ...f, halfCarts: e.target.value.replace(/[^0-9]/g, "") }))} />
                            </FormGroup>
                            <FormGroup label="No. of SMU Units" hint="Standard Modular Units for small items">
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.smu ?? ""}
                                onChange={e => setGalleyForm(f => ({ ...f, smu: e.target.value.replace(/[^0-9]/g, "") }))} />
                            </FormGroup>
                            <FormGroup label="No. of Miscellaneous Storage" hint="Overhead bins, drawers, etc.">
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.stowage ?? ""}
                                onChange={e => setGalleyForm(f => ({ ...f, stowage: e.target.value.replace(/[^0-9]/g, "") }))} />
                            </FormGroup>
                          </div>

                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#8aaacf", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                              Galley Image <span style={{ color: "#3d5a7a", fontWeight: 400, textTransform: "none", fontSize: 11 }}>(Optional)</span>
                            </div>
                            <div
                              style={{ border: "2px dashed rgba(40,80,140,0.6)", borderRadius: 8, padding: "28px 20px", textAlign: "center", cursor: "pointer", background: "#0c1526" }}
                              onClick={() => document.getElementById("galleyImageInput").click()}
                              onMouseOver={e => e.currentTarget.style.borderColor = "rgba(76,195,230,0.5)"}
                              onMouseOut={e => e.currentTarget.style.borderColor = "rgba(40,80,140,0.6)"}
                            >
                              <input id="galleyImageInput" type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
                                onChange={e => {
                                  const file = e.target.files?.[0];
                                  if (file) setGalleyForm(f => ({ ...f, imageFile: file, imageName: file.name }));
                                }} />
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3d5a7a" strokeWidth="1.5" style={{ marginBottom: 10 }}>
                                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                              </svg>
                              {galleyForm.imageName ? (
                                <div style={{ fontSize: 13, color: "#4cc3e6" }}>{galleyForm.imageName}</div>
                              ) : (
                                <>
                                  <div style={{ fontSize: 13, color: "#8aaacf", marginBottom: 6 }}>Click to upload galley layout image</div>
                                  <div style={{ fontSize: 11, color: "#3d5a7a", lineHeight: 1.6 }}>
                                    Format: JPG, PNG or WebP · Aspect ratio: <strong style={{ color: "#4cc3e6" }}>16:9</strong> recommended<br />
                                    Max size: <strong style={{ color: "#4cc3e6" }}>5 MB</strong> · Min resolution: <strong style={{ color: "#4cc3e6" }}>1280 × 720px</strong><br />
                                    Use a top-down galley diagram or aircraft cross-section view
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button className="btn btn-ghost" onClick={() => { setGalleyModal(false); setEditingGalley(null); setGalleyForm({ name: "", position: "fwd", classCodes: ["ecy"], fullCarts: 0, halfCarts: 0, smu: 0, stowage: 0, imageFile: null, imageName: "" }); }} disabled={savingGalley}>Cancel</button>
                          <button className="btn btn-primary" onClick={handleAddGalley} disabled={savingGalley || !galleyForm.name.trim()}>
                            {savingGalley ? "Saving…" : editingGalley ? "Save Changes →" : "Add Galley →"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                
              </div>
              </div>
  );
};

export default CateringListing;
